import {
  Action,
  AssistantType,
  ConstraintInterface,
  GoalApiService,
  GoalInterface,
  ListGoalsRequestSortOptionsInterface,
  Namespace,
  NamespaceInterface,
} from '@vendasta/ai-assistants';
import { PagedListRequestInterface, PagedResponseInterface, PaginatedAPIInterface } from '@vendasta/galaxy/table';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { GoalNamespaceType } from '../../../core/ai-assistant.constants';
import { getNamespace } from '../../../core/services/ai-assistant-utils';
import { createConstraintFilters } from '../../../core/services/constraint-utils';
import {
  AiAssistantConfig,
  AssistantProductMap,
  AssistantTypeProductMap,
  ProductIcon,
} from '../../../core/interfaces/config.interface';

export interface GoalInterfaceWithProduct extends GoalInterface {
  productIcon?: ProductIcon;
  isCustom: boolean;
}

export class AiCapabilityTableDataSource
  implements PaginatedAPIInterface<GoalInterfaceWithProduct, void, ListGoalsRequestSortOptionsInterface>
{
  constructor(
    private readonly goalApiService: GoalApiService,
    private readonly assistantType: AssistantType,
    private readonly accountGroupId$: Observable<string>,
    private readonly partnerId$: Observable<string>,
    private readonly config: AiAssistantConfig,
  ) {}

  get(
    req: PagedListRequestInterface<void, ListGoalsRequestSortOptionsInterface>,
  ): Observable<PagedResponseInterface<GoalInterfaceWithProduct>> {
    const { cursor, pageSize } = req.pagingOptions ?? {};
    const { text } = req.searchOptions ?? {};
    const sortOptions = req.sorting ?? [];

    const assistantProductMap$: Observable<AssistantProductMap> =
      this.config.assistantProductMap$ ??
      of({ assistantTypeMap: {}, capabilityIdMap: {} } satisfies AssistantProductMap);

    return combineLatest([this.accountGroupId$, this.partnerId$, assistantProductMap$]).pipe(
      switchMap(([accountGroupId, partnerId, assistantProductMap]) => {
        const namespace = getNamespace(accountGroupId, partnerId);

        if (!namespace)
          return of({
            data: [],
            pagingMetadata: {
              nextCursor: '',
              hasMore: false,
            },
          });

        const namespaces: Namespace[] = [namespace, new Namespace({ globalNamespace: {} })];

        const constraintFilters = createConstraintFilters(partnerId, accountGroupId, this.assistantType);

        return this.goalApiService
          .list({
            filters: {
              namespaces: namespaces,
              searchTerm: text,
              sortOptions: sortOptions,
              omitOverrides: true,
              ...(constraintFilters.length > 0 && { constraintFilters }),
            },
            pagingOptions: {
              cursor,
              pageSize,
            },
          })
          .pipe(
            map((res) => {
              const enrichedGoals = (res.goals ?? []).map((goal) => ({
                ...goal,
                isCustom: this.isCustomCapability(goal),
                productIcon: goal.id
                  ? (this.config.capabilityProductMap?.[goal.id] ??
                    assistantProductMap.capabilityIdMap?.[goal.id] ??
                    this.getProductIconFromAssistantType(goal.constraints, assistantProductMap.assistantTypeMap))
                  : undefined,
              }));

              return {
                data: enrichedGoals,
                pagingMetadata: {
                  nextCursor: res.metadata?.nextCursor ?? '',
                  hasMore: res.metadata?.hasMore ?? false,
                },
              };
            }),
          );
      }),
    );
  }

  private getAssistantTypeFromConstraints(constraints?: ConstraintInterface[]): AssistantType | undefined {
    if (!constraints) return undefined;

    const assignConstraint = constraints.find(
      (c) => c.action === Action.ACTION_ASSIGN && c.scope?.assistantType !== undefined,
    );

    return assignConstraint?.scope?.assistantType;
  }

  private getProductIconFromAssistantType(
    constraints?: ConstraintInterface[],
    assistantTypeProductMap?: AssistantTypeProductMap,
  ): ProductIcon | undefined {
    const assistantType = this.getAssistantTypeFromConstraints(constraints);
    if (assistantType == null) return undefined;

    return assistantTypeProductMap?.[assistantType];
  }

  private getNamespaceType(namespace: NamespaceInterface | undefined): GoalNamespaceType {
    if (!namespace) return GoalNamespaceType.Local;

    if (namespace.globalNamespace) return GoalNamespaceType.Global;
    if (namespace.systemNamespace) return GoalNamespaceType.System;

    return GoalNamespaceType.Local;
  }

  private isCustomCapability(goal: GoalInterface): boolean {
    const namespaceType = this.getNamespaceType(goal.namespace);

    return namespaceType === GoalNamespaceType.Local && !goal.managed;
  }
}
